import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Radar, Loader2, MapPin, Info } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const PREFS = [{ key: "one_on_one", label: "1:1" }, { key: "group", label: "Group" }];
const FIELD = "mt-1 w-full bg-[#141414] border border-[#262626] focus:border-[#CCFF00] focus:outline-none p-2 text-sm text-white";

export function LookingForSession() {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [pref, setPref] = useState("");
  const [location, setLocation] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/player-interest/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  useEffect(() => { load(); }, []);

  const start = () => {
    if (status && !status.can_submit) { toast.info(status.message); return; }
    setPref(""); setNote(""); setLocation(status?.home_location || ""); setOpen(true);
  };
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/player-interest", { preference: pref, location: location.trim(), note: note.trim() });
      toast.success("Sent — coaches near you have been notified");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send"); }
    finally { setBusy(false); }
  };

  const locked = status && !status.can_submit;
  return (
    <>
      <section className="card-surface p-5 flex flex-wrap items-center gap-4 fade-up" style={{ borderLeft: "3px solid #CCFF00" }} data-testid="looking-for-session-card">
        <div className="w-12 h-12 flex items-center justify-center shrink-0" style={{ background: "#CCFF00", color: "#0A0A0A" }}>
          <Radar className="w-6 h-6" strokeWidth={2.25} />
        </div>
        <div className="flex-1 min-w-[180px]">
          <div className="label-mono text-[#CCFF00]">OPEN TO PLAY?</div>
          <div className="text-sm font-semibold mt-0.5">Let coaches know you're looking for a session.</div>
          {locked && <div className="text-xs text-[#A3A3A3] mt-1" data-testid="looking-for-session-cooldown">{status.message}</div>}
        </div>
        <button type="button" onClick={start} data-testid="looking-for-session-btn"
          className={`btn-volt px-5 py-2.5 text-xs inline-flex items-center gap-2 ${locked ? "opacity-50" : ""}`}>
          Looking for a session
        </button>
      </section>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F0F0F] border border-[#262626] max-w-md text-[#E5E5E5]" data-testid="looking-for-session-dialog">
          <DialogHeader>
            <DialogTitle>Looking for a session</DialogTitle>
            <DialogDescription className="text-sm text-[#A3A3A3] pt-1">Every coach on Rally Lab will get a heads-up.</DialogDescription>
          </DialogHeader>
          <div>
            <span className="label-mono text-[#A3A3A3]">Session preference</span>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {PREFS.map((p) => (
                <button key={p.key} type="button" onClick={() => setPref(p.key)} data-testid={`interest-pref-${p.key}`}
                  className={`py-3 text-sm font-bold uppercase tracking-wider border transition-colors ${pref === p.key ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-[#404040] text-[#A3A3A3] hover:text-white hover:border-white"}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="label-mono text-[#A3A3A3] inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> Location</span>
            <input value={location} onChange={(e) => setLocation(e.target.value)} maxLength={120} placeholder="e.g. Leeds"
              data-testid="interest-location" className={FIELD} />
          </label>
          <label className="block">
            <span className="label-mono text-[#A3A3A3]">Note (optional)</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder="e.g. weekday evenings"
              data-testid="interest-note" className={FIELD} />
          </label>
          <div className="flex gap-2 p-3 border border-[#FF9F1C]/40 bg-[#FF9F1C]/5 text-xs text-[#E5E5E5]" data-testid="interest-informal-label">
            <Info className="w-4 h-4 shrink-0 text-[#FF9F1C]" />
            <span>This is an informal expression of interest, not a formal booking request. For formal requests use the{" "}
              <Link to="/request/new" className="underline text-[#CCFF00]" data-testid="interest-booking-form-link">booking request form</Link>.</span>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} data-testid="interest-cancel"
              className="px-4 py-2 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white">Cancel</button>
            <button type="button" onClick={submit} disabled={busy || !pref || !location.trim()} data-testid="interest-submit"
              className="min-h-[40px] px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#CCFF00] text-black inline-flex items-center gap-1.5 disabled:opacity-40">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Send
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
